/**
 * Lectura segura de datos del usuario en sesión (`localStorage.userData`).
 * El login persiste el objeto `user` del API (incl. `whsCode`, `whsName` cuando el backend los envía).
 */

/** Dominios de correo de sesión → país en Carga Mayorista (prioridad sobre Excel/RUT). */
export const WHOLESALE_SESSION_PERU_EMAIL_DOMAINS = ['iacperu.com'];
export const WHOLESALE_SESSION_CHILE_EMAIL_DOMAINS = ['iacchile.com'];

/**
 * @param {string} email
 * @param {string[]} domains
 * @returns {boolean}
 */
function emailDomainMatchesWholesale(email, domains) {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = email.slice(at + 1).toLowerCase().trim();
  return domains.some((d) => domain === d || domain.endsWith(`.${d}`));
}

/**
 * País wholesale desde correo (`@iacperu.com` → PE, `@iacchile.com` → CL).
 * @param {string|null|undefined} email
 * @returns {'PE'|'CL'|null}
 */
export function inferWholesaleCountryFromEmail(email) {
  const e = String(email ?? '').trim().toLowerCase();
  if (!e || !e.includes('@')) return null;
  if (emailDomainMatchesWholesale(e, WHOLESALE_SESSION_PERU_EMAIL_DOMAINS)) return 'PE';
  if (emailDomainMatchesWholesale(e, WHOLESALE_SESSION_CHILE_EMAIL_DOMAINS)) return 'CL';
  return null;
}

/**
 * @returns {string} Email del usuario en sesión, o cadena vacía.
 */
export function getLoggedInUserEmail() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return '';
    const user = JSON.parse(raw);
    const email = user?.email;
    if (email != null && String(email).trim()) return String(email).trim();
    const username = user?.username;
    if (username != null && String(username).includes('@')) return String(username).trim();
    return '';
  } catch {
    return '';
  }
}

/**
 * País del operador en Carga Mayorista según email de login.
 * Demo u otros dominios → `null` (fallback Excel + RUC en `isBulkPeruWholesaleContext`).
 * @returns {'PE'|'CL'|null}
 */
export function getWholesaleCountryFromSession() {
  return inferWholesaleCountryFromEmail(getLoggedInUserEmail());
}

/**
 * @returns {string} Código de bodega SAP asignado al usuario, o cadena vacía.
 */
export function getUserWhsCode() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return '';
    const user = JSON.parse(raw);
    const code = user?.whsCode;
    return code != null ? String(code).trim() : '';
  } catch {
    return '';
  }
}

/**
 * @returns {string} Nombre descriptivo de la bodega, o cadena vacía.
 */
export function getUserWhsName() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return '';
    const user = JSON.parse(raw);
    const name = user?.whsName;
    return name != null ? String(name).trim() : '';
  } catch {
    return '';
  }
}

/**
 * @returns {string} Id del usuario en sesión (UUID habitual), o cadena vacía.
 */
export function getLoggedInUserId() {
  try {
    const raw = localStorage.getItem('userData');
    if (!raw) return '';
    const user = JSON.parse(raw);
    const id = user?.id;
    return id != null ? String(id).trim() : '';
  } catch {
    return '';
  }
}
